import { supabase, authHelpers } from '../config/supabaseClient';

// Matches the advertised plans: Free / Pay-Per-Video / Premium
export const TIER_LIMITS = {
  free:         { videosPerMonth: 1,    maxDurationSecs: 300, questions: 10, templates: false, videosLabel: 'per month' },
  oneoff:       { videosPerMonth: 1,    maxDurationSecs: 300, questions: 10, templates: true,  videosLabel: 'on this purchase' },
  subscription: { videosPerMonth: null, maxDurationSecs: 300, questions: 10, templates: true,  videosLabel: null },
};

export const normalizeTier = (tier) => {
  if (tier === 'premium') return 'subscription';
  if (tier === 'oneoff' || tier === 'subscription') return tier;
  return 'free';
};

export const isPaidTier = (tier) => {
  const t = normalizeTier(tier);
  return t === 'oneoff' || t === 'subscription';
};

/**
 * Fetch the current user's profile from Supabase.
 * Returns null if not logged in or profile not found.
 */
export const fetchProfile = async () => {
  const user = await authHelpers.getUser().catch(() => null);
  if (!user) return null;

  const { data, error } = await supabase
    .from('profiles')
    .select('*')
    .eq('id', user.id)
    .single();

  if (error) {
    console.error('fetchProfile error:', error.message);
    return null;
  }
  return data;
};

/**
 * Upsert a profile row (creates if missing, updates if exists).
 */
export const upsertProfile = async (fields = {}) => {
  const user = await authHelpers.getUser().catch(() => null);
  if (!user) throw new Error('Not authenticated');

  const email = fields.email || user.email;
  if (!email) {
    throw new Error('Cannot save profile without an email address');
  }

  const payload = {
    id: user.id,
    ...fields,
    // Always win over any missing/null email in fields — profiles.email is NOT NULL
    email,
  };
  if (!payload.full_name && user.user_metadata?.full_name) {
    payload.full_name = user.user_metadata.full_name;
  }

  const { data, error } = await supabase
    .from('profiles')
    .upsert(payload, { onConflict: 'id' })
    .select()
    .single();

  if (error) throw error;
  return data;
};

/**
 * Set the user's subscription tier in Supabase.
 * Also resets oneoff_videos_used when switching to oneoff.
 */
export const setTierInDB = async (tier) => {
  const updates = { subscription_tier: tier, updated_at: new Date().toISOString() };
  if (tier === 'oneoff') updates.oneoff_videos_used = 0;
  return upsertProfile(updates);
};

/**
 * Check whether the user can create another video based on their tier.
 * Returns { allowed: boolean, reason: string }
 */
export const checkVideoLimitDB = async () => {
  const profile = await fetchProfile();
  if (!profile) return { allowed: false, reason: 'not_authenticated' };

  const tier = normalizeTier(profile.subscription_tier);

  if (tier === 'subscription') return { allowed: true, reason: 'unlimited' };

  if (tier === 'oneoff') {
    const used = profile.oneoff_videos_used || 0;
    return used < 1
      ? { allowed: true, reason: 'oneoff_available' }
      : { allowed: false, reason: 'oneoff_used', message: 'You have already used your one-off video. Purchase again to create another.' };
  }

  // free tier — check monthly reset
  const now = new Date();
  const lastReset = new Date(profile.videos_reset_date || now);
  const needsReset =
    now.getMonth() !== lastReset.getMonth() ||
    now.getFullYear() !== lastReset.getFullYear();

  const count = needsReset ? 0 : (profile.videos_this_month || 0);
  const limit = TIER_LIMITS.free.videosPerMonth;

  return count < limit
    ? { allowed: true, reason: 'under_limit', remaining: limit - count }
    : { allowed: false, reason: 'limit_reached', message: `You've used all ${limit} free videos this month.` };
};

/**
 * Increment the video count in Supabase after a video is created.
 */
export const incrementVideoCountDB = async () => {
  const profile = await fetchProfile();
  if (!profile) return;

  const tier = normalizeTier(profile.subscription_tier);
  if (tier === 'subscription') return; // no tracking needed

  const now = new Date();
  const lastReset = new Date(profile.videos_reset_date || now);
  const needsReset =
    now.getMonth() !== lastReset.getMonth() ||
    now.getFullYear() !== lastReset.getFullYear();

  if (tier === 'oneoff') {
    await supabase
      .from('profiles')
      .update({ oneoff_videos_used: (profile.oneoff_videos_used || 0) + 1, updated_at: now.toISOString() })
      .eq('id', profile.id);
    return;
  }

  // free
  await supabase
    .from('profiles')
    .update({
      videos_this_month: needsReset ? 1 : (profile.videos_this_month || 0) + 1,
      videos_reset_date: needsReset ? now.toISOString() : profile.videos_reset_date,
      updated_at: now.toISOString(),
    })
    .eq('id', profile.id);
};

/**
 * Get remaining video count for display.
 * Returns a number or the string 'unlimited'.
 */
export const getRemainingVideosDB = async () => {
  const profile = await fetchProfile();
  if (!profile) return 0;

  const tier = normalizeTier(profile.subscription_tier);
  if (tier === 'subscription') return 'unlimited';

  if (tier === 'oneoff') return Math.max(0, 1 - (profile.oneoff_videos_used || 0));

  const now = new Date();
  const lastReset = new Date(profile.videos_reset_date || now);
  const needsReset =
    now.getMonth() !== lastReset.getMonth() ||
    now.getFullYear() !== lastReset.getFullYear();

  const count = needsReset ? 0 : (profile.videos_this_month || 0);
  return Math.max(0, TIER_LIMITS.free.videosPerMonth - count);
};

/**
 * Check whether the user can create another greeting card based on their tier.
 * Returns { allowed: boolean, reason: string }
 */
export const checkGreetingLimitDB = async () => {
  const profile = await fetchProfile();
  if (!profile) return { allowed: false, reason: 'not_authenticated' };

  const tier = normalizeTier(profile.subscription_tier);

  if (tier === 'subscription' || tier === 'oneoff') return { allowed: true, reason: 'unlimited' };

  // free tier — check monthly reset
  const now = new Date();
  const lastReset = new Date(profile.greeting_cards_reset_date || now);
  const needsReset =
    now.getMonth() !== lastReset.getMonth() ||
    now.getFullYear() !== lastReset.getFullYear();

  const count = needsReset ? 0 : (profile.greeting_cards_this_month || 0);
  const limit = 3; // Free tier gets 3 greeting cards per month

  return count < limit
    ? { allowed: true, reason: 'under_limit', remaining: limit - count }
    : { allowed: false, reason: 'limit_reached', message: `You've used all ${limit} free greeting cards this month.` };
};

/**
 * Increment the greeting card count in Supabase after a greeting is created.
 */
export const incrementGreetingCountDB = async () => {
  const profile = await fetchProfile();
  if (!profile) return;

  const tier = normalizeTier(profile.subscription_tier);
  if (tier === 'subscription' || tier === 'oneoff') return;

  const now = new Date();
  const lastReset = new Date(profile.greeting_cards_reset_date || now);
  const needsReset =
    now.getMonth() !== lastReset.getMonth() ||
    now.getFullYear() !== lastReset.getFullYear();

  // free tier - increment greeting card count
  await supabase
    .from('profiles')
    .update({
      greeting_cards_this_month: needsReset ? 1 : (profile.greeting_cards_this_month || 0) + 1,
      greeting_cards_reset_date: needsReset ? now.toISOString() : profile.greeting_cards_reset_date,
      updated_at: now.toISOString(),
    })
    .eq('id', profile.id);
};

/**
 * Get remaining greeting card count for display.
 * Returns a number or the string 'unlimited'.
 */
export const getRemainingGreetingsDB = async () => {
  const profile = await fetchProfile();
  if (!profile) return 0;

  const tier = normalizeTier(profile.subscription_tier);
  if (tier === 'subscription' || tier === 'oneoff') return 'unlimited';

  const now = new Date();
  const lastReset = new Date(profile.greeting_cards_reset_date || now);
  const needsReset =
    now.getMonth() !== lastReset.getMonth() ||
    now.getFullYear() !== lastReset.getFullYear();

  const count = needsReset ? 0 : (profile.greeting_cards_this_month || 0);
  const limit = 3; // Free tier gets 3 greeting cards per month
  return Math.max(0, limit - count);
};
